import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import AccountSelect, { decodeAccountSelection, useAccountOptions } from "./AccountSelect";

// DD-MM-YYYY, the conventional Indian accounting-document date format - used
// on printed vouchers (VoucherPrintLayout.jsx) rather than the raw ISO
// "YYYY-MM-DD" the on-screen forms/tables use internally.
export function formatDateDMY(isoDate) {
  const s = String(isoDate || "");
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s || "-";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export function formatINR(value) {
  return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Phase 5 correction (item 12 - Currency Formatting): one centralized
// Indian-currency formatter ("₹ 232.00") used everywhere in the voucher/
// payment screens, replacing the earlier mix of "Rs 232.00" and "₹232".
export function formatCurrency(value) {
  return `₹ ${formatINR(value)}`;
}

// Phase 5 "Fix Voucher Printing" correction (item 10 - Amount in Words): one
// centralized Indian-numbering (Thousand/Lakh/Crore, not Million/Billion)
// amount-to-words implementation, so every voucher print layout (Payment,
// Receipt, Contra, Journal, Purchase, Credit/Debit Note - see
// VoucherPrintLayout.jsx) shares exactly one piece of logic rather than each
// screen inventing its own.
const IN_WORDS_ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const IN_WORDS_TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

function _twoDigitWords(n) {
  if (n < 20) return IN_WORDS_ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return IN_WORDS_TENS[tens] + (ones ? ` ${IN_WORDS_ONES[ones]}` : "");
}
function _threeDigitWords(n) {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  let out = "";
  if (hundreds) out += `${IN_WORDS_ONES[hundreds]} Hundred`;
  if (rest) out += (out ? " " : "") + _twoDigitWords(rest);
  return out;
}

/** Whole-number Indian-numbering words (Crore / Lakh / Thousand / Hundred) -
 * e.g. 1234567 -> "Twelve Lakh Thirty-Four Thousand Five Hundred Sixty-Seven". */
export function numberToIndianWords(value) {
  let n = Math.floor(Math.abs(Number(value) || 0));
  if (n === 0) return "Zero";
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  const parts = [];
  if (crore) parts.push(`${_threeDigitWords(crore)} Crore`);
  if (lakh) parts.push(`${_threeDigitWords(lakh)} Lakh`);
  if (thousand) parts.push(`${_threeDigitWords(thousand)} Thousand`);
  if (hundred) parts.push(_threeDigitWords(hundred));
  return parts.join(" ");
}

/** "Rupees Four Hundred Fifty-Four Only" / "...and Fifty Paise Only" - the
 * full printable amount-in-words line used on every voucher print layout. */
export function formatAmountInWords(amount) {
  const abs = Math.abs(Number(amount) || 0);
  const rupees = Math.floor(abs);
  const paise = Math.round((abs - rupees) * 100);
  let words = `Rupees ${numberToIndianWords(rupees)}`;
  if (paise > 0) words += ` and ${numberToIndianWords(paise)} Paise`;
  return `${words} Only`;
}

// Shared with VoucherViewPage.jsx and BankVoucherFormPage.jsx's Recent
// Vouchers tables, per the Phase 5 Payment Accounting Logic correction: one
// definition of "who/what is the business-facing party/purpose side" and
// "who/what is the bank/cash side" of a two-line Payment/Receipt/Contra, so
// the detail view and the list view can never disagree.
export function partySideLabel(line) {
  if (!line) return "-";
  return line.vendorLabel || line.partyName || line.ledgerName || "-";
}
export function bankSideLabel(line) {
  if (!line) return "-";
  return line.bankAccountLabel || line.ledgerName || "-";
}

// Phase 5 correction item 10 - lightweight party categorization: appended
// as "(Employee / Staff)" etc. next to a free-text Party name, never for a
// real Vendor (whose own label already makes the category obvious) and
// never when left as the default "other" (nothing meaningful to add).
const PARTY_TYPE_SUFFIX_LABELS = { member: "Member", employee: "Employee / Staff", other: "" };
export function partySideLabelWithType(line) {
  const label = partySideLabel(line);
  if (!line || !line.partyName || line.vendorLabel) return label;
  const suffix = PARTY_TYPE_SUFFIX_LABELS[line.partyType];
  return suffix ? `${label} (${suffix})` : label;
}

/**
 * Derives the business-facing "Paid To / Payment For / Paid From" (or the
 * Receipt/Contra equivalents) purely from voucherType + each line's
 * debit/credit POSITION - exactly how BankVoucherFormPage.jsx always
 * constructs these two-line vouchers - never from fragile ledger-name
 * heuristics. Returns null for anything that isn't an exactly-2-line
 * PAYMENT/RECEIPT/CONTRA voucher (Journal, multi-line, Purchase, etc. fall
 * back to their own generic rendering) - except the Phase 12 Member Receipt
 * 3-line case handled explicitly below.
 */
export function deriveTransactionDetails(voucher) {
  const lines = voucher.lines || [];
  if (!["PAYMENT", "RECEIPT", "CONTRA"].includes(voucher.voucherType)) return null;

  // Phase 12 (Receipt Voucher) - a standalone Member Receipt that exceeds
  // the member's outstanding splits the excess into a separate Member
  // Advance credit line (see server.py's create_member_receipt_voucher),
  // so this one case legitimately has 3 lines (Bank/Cash debit + Member
  // Receivable credit + Member Advance credit) instead of the usual 2.
  if (voucher.voucherType === "RECEIPT" && lines.length === 3) {
    const bankLine = lines.find((l) => l.debit > 0) || lines[0];
    const creditLines = lines.filter((l) => l.credit > 0);
    const first = creditLines[0];
    return {
      amount: bankLine.debit || 0,
      paidTo: partySideLabel(first),
      paymentFor: creditLines.map((l) => l.ledgerName || "-").join(" + "),
      paidFrom: bankSideLabel(bankLine),
      rows: [
        { label: "Received From", value: partySideLabelWithType(first) },
        ...creditLines.map((l) => ({ label: l.ledgerName || "Credit", value: formatCurrency(l.credit) })),
        { label: "Received Into", value: bankSideLabel(bankLine) },
      ],
    };
  }

  if (lines.length !== 2) return null;

  if (voucher.voucherType === "PAYMENT") {
    const accountLine = lines.find((l) => l.debit > 0) || lines[0];
    const bankLine = lines.find((l) => l.credit > 0) || lines[1];
    return {
      amount: accountLine.debit || bankLine.credit || 0,
      paidTo: partySideLabel(accountLine),
      paymentFor: accountLine.ledgerName || "-",
      paidFrom: bankSideLabel(bankLine),
      rows: [
        { label: "Paid To", value: partySideLabelWithType(accountLine) },
        { label: "Payment For", value: accountLine.ledgerName || "-" },
        { label: "Paid From", value: bankSideLabel(bankLine) },
      ],
    };
  }
  if (voucher.voucherType === "RECEIPT") {
    const bankLine = lines.find((l) => l.debit > 0) || lines[0];
    const accountLine = lines.find((l) => l.credit > 0) || lines[1];
    return {
      amount: bankLine.debit || accountLine.credit || 0,
      paidTo: partySideLabel(accountLine),
      paymentFor: accountLine.ledgerName || "-",
      paidFrom: bankSideLabel(bankLine),
      rows: [
        { label: "Received From", value: partySideLabelWithType(accountLine) },
        { label: "Received For", value: accountLine.ledgerName || "-" },
        { label: "Received Into", value: bankSideLabel(bankLine) },
      ],
    };
  }
  const toLine = lines.find((l) => l.debit > 0) || lines[0];
  const fromLine = lines.find((l) => l.credit > 0) || lines[1];
  return {
    amount: toLine.debit || fromLine.credit || 0,
    paidTo: bankSideLabel(toLine),
    paymentFor: "Fund Transfer",
    paidFrom: bankSideLabel(fromLine),
    rows: [
      { label: "Transferred From", value: bankSideLabel(fromLine) },
      { label: "Transferred To", value: bankSideLabel(toLine) },
    ],
  };
}

export function blankGridRow() {
  return { key: Math.random().toString(36).slice(2), selection: "", debit: "", credit: "", partyName: "", referenceNo: "", narration: "" };
}

/**
 * Multi-line debit/credit grid shared by Journal, Vendor Bill, Credit Note,
 * Debit Note, and generic Draft editing - the "advanced" voucher screens
 * where Phase 5 requires real multi-line support (any number of debit lines
 * + any number of credit lines, not a forced single-debit/single-credit
 * pair). Each row picks one account via the unified AccountSelect (Cash,
 * Bank Account, Vendor, or a plain Ledger) and enters either a debit or a
 * credit amount - never both, enforced in the UI and re-validated by the
 * backend regardless.
 */
export default function LineGrid({ rows, onChange, minRows = 2, hideCash = false, hideBankAccounts = false, context }) {
  const options = useAccountOptions();

  const updateRow = (key, patch) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...rows, blankGridRow()]);
  const removeRow = (key) => {
    if (rows.length <= minRows) return;
    onChange(rows.filter((r) => r.key !== key));
  };

  const totalDebit = rows.reduce((sum, r) => sum + (Number(r.debit) || 0), 0);
  const totalCredit = rows.reduce((sum, r) => sum + (Number(r.credit) || 0), 0);
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-xs uppercase tracking-[0.1em] text-[#6B7280]">
              <th className="px-2 py-2">Ledger</th>
              <th className="px-2 py-2 text-right">Debit</th>
              <th className="px-2 py-2 text-right">Credit</th>
              <th className="px-2 py-2">Party / Member / Vendor</th>
              <th className="px-2 py-2">Reference</th>
              <th className="px-2 py-2">Line narration</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-b border-[#F3F4F6]">
                <td className="px-2 py-1.5 min-w-[200px]">
                  <AccountSelect value={row.selection} onChange={(v) => updateRow(row.key, { selection: v })} options={options} hideCash={hideCash} hideBankAccounts={hideBankAccounts} context={context} />
                </td>
                <td className="px-2 py-1.5 w-28">
                  <Input type="number" min="0" step="0.01" value={row.debit} onChange={(e) => updateRow(row.key, { debit: e.target.value, credit: e.target.value ? "" : row.credit })} className="text-right" />
                </td>
                <td className="px-2 py-1.5 w-28">
                  <Input type="number" min="0" step="0.01" value={row.credit} onChange={(e) => updateRow(row.key, { credit: e.target.value, debit: e.target.value ? "" : row.debit })} className="text-right" />
                </td>
                <td className="px-2 py-1.5 min-w-[140px]">
                  <Input value={row.partyName} onChange={(e) => updateRow(row.key, { partyName: e.target.value })} />
                </td>
                <td className="px-2 py-1.5 min-w-[120px]">
                  <Input value={row.referenceNo} onChange={(e) => updateRow(row.key, { referenceNo: e.target.value })} />
                </td>
                <td className="px-2 py-1.5 min-w-[160px]">
                  <Input value={row.narration} onChange={(e) => updateRow(row.key, { narration: e.target.value })} />
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Button type="button" variant="outline" size="sm" disabled={rows.length <= minRows} onClick={() => removeRow(row.key)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRow}><Plus className="mr-2 h-3.5 w-3.5" /> Add Row</Button>

      <div className="flex flex-wrap justify-end gap-6 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm">
        <div>Total Debit: <span className="font-semibold text-[#111827]">{formatCurrency(totalDebit)}</span></div>
        <div>Total Credit: <span className="font-semibold text-[#111827]">{formatCurrency(totalCredit)}</span></div>
        <div>Difference: <span className={`font-semibold ${difference === 0 ? "text-emerald-600" : "text-red-600"}`}>{formatCurrency(Math.abs(difference))}</span></div>
      </div>
    </div>
  );
}

export function gridRowsToLines(rows) {
  return rows
    .filter((r) => r.selection && (Number(r.debit) > 0 || Number(r.credit) > 0))
    .map((r) => {
      const target = decodeAccountSelection(r.selection) || {};
      return {
        ...target,
        debit: Number(r.debit) || 0,
        credit: Number(r.credit) || 0,
        partyName: r.partyName || "",
        referenceNo: r.referenceNo || "",
        narration: r.narration || "",
      };
    });
}
